import { memo, useState, useEffect } from 'react';
import { 
  Settings, 
  X, 
  Upload, 
  Sparkles, 
  Video, 
  Users, 
  Eye,
  Trash2,
  Copy,
  Link,
  Unlink,
  Play,
  Download,
  Film,
  Wand2,
  Music,
  Pencil,
  Star
} from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';

const NodeProperties = memo(() => {
  const selectedNode = useWorkflowStore((state) => state.selectedNode);
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const setSelectedNode = useWorkflowStore((state) => state.setSelectedNode);

  // Get current node data
  const currentNode = nodes.find((n) => n.id === selectedNode?.id);
  const nodeData = currentNode?.data || {};

  // Get connections info
  const inputConnections = edges.filter((e) => e.target === selectedNode?.id);
  const outputConnections = edges.filter((e) => e.source === selectedNode?.id);

  const getNodeIcon = (type) => {
    switch (type) {
      case 'uploadImage': return Upload;
      case 'imageEnhancement': return Sparkles;
      case 'videoGeneration': return Video;
      case 'nanoBanana': return Wand2;
      case 'frameExtractor': return Film;
      case 'lyria3pro': return Music;
      case 'imageEdit': return Pencil;
      case 'banana2pro': return Star;
      case 'imagen4Ultra': return Sparkles;
      case 'seedance25': return Video;
      case 'pixverseV6': return Film;
      case 'eleV2': return Music;
      case 'elTraductor': return Music;
      case 'lipSync': return Video;
      case 'musicPro': return Music;
      case 'avatarFast': return Users;
      case 'outputPreview': return Eye;
      default: return Settings;
    }
  };

  const getNodeTitle = (type) => {
    switch (type) {
      case 'uploadImage': return 'Subir Imagen';
      case 'imageEnhancement': return 'Mejorar Imagen';
      case 'videoGeneration': return 'Crear Video';
      case 'nanoBanana': return 'Nano Banana';
      case 'frameExtractor': return 'Frame Extractor';
      case 'lyria3pro': return 'Lyria 3 Full Song';
      case 'imageEdit': return 'Image Edit';
      case 'banana2pro': return 'Editor Banana 2 Pro';
      case 'imagen4Ultra': return 'Image - 4Ultra';
      case 'seedance25': return 'Video seedance-2.5';
      case 'pixverseV6': return 'Pixverse V6';
      case 'eleV2': return 'EleV2';
      case 'elTraductor': return 'El_Traductor';
      case 'lipSync': return 'LipSinc';
      case 'musicPro': return 'Music Pro';
      case 'avatarFast': return 'Avatar Fast';
      case 'outputPreview': return 'Vista Previa';
      default: return 'Nodo';
    }
  };

  const getNodeColor = (type) => {
    switch (type) {
      case 'uploadImage': return '#3b82f6';
      case 'imageEnhancement': return '#10b981';
      case 'videoGeneration': return '#8b5cf6';
      case 'nanoBanana': return '#f59e0b';
      case 'frameExtractor': return '#0ea5e9';
      case 'lyria3pro': return '#d946ef';
      case 'imageEdit': return '#f472b6';
      case 'banana2pro': return '#fbbf24';
      case 'imagen4Ultra': return '#818cf8';
      case 'seedance25': return '#22d3ee';
      case 'pixverseV6': return '#fb7185';
      case 'eleV2': return '#4ade80';
      case 'elTraductor': return '#a3e635';
      case 'lipSync': return '#e879f9';
      case 'musicPro': return '#2dd4bf';
      case 'avatarFast': return '#facc15';
      case 'outputPreview': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const handleDelete = () => {
    if (selectedNode) {
      deleteNode(selectedNode.id);
      setSelectedNode(null);
    }
  };

  const handleDuplicate = () => {
    // TODO: Implement node duplication
  };

  if (!selectedNode) {
    return (
      <div className="node-properties" data-testid="node-properties">
        <div className="node-properties-header">
          <h3>
            <Settings size={16} />
            Propiedades
          </h3>
        </div>
        <div className="node-properties-empty">
          <Settings size={32} />
          <p>Selecciona un nodo</p>
          <span>Haz clic en un nodo del canvas para ver sus propiedades</span>
        </div>
      </div>
    );
  }

  const NodeIcon = getNodeIcon(selectedNode.type);
  const nodeColor = getNodeColor(selectedNode.type);

  return (
    <div className="node-properties" data-testid="node-properties">
      {/* Header */}
      <div className="node-properties-header">
        <h3>
          <Settings size={16} />
          Propiedades
        </h3>
        <div className="properties-header-actions">
          <button
            className="properties-delete-btn"
            onClick={handleDelete}
            title="Eliminar nodo"
            data-testid="properties-delete-btn"
          >
            <Trash2 size={14} />
            <span>Eliminar</span>
          </button>
          <button
            className="properties-close-btn"
            onClick={() => setSelectedNode(null)}
            title="Cerrar panel"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Node Info */}
      <div className="node-info-section">
        <div className="node-info-header" style={{ borderLeftColor: nodeColor }}>
          <NodeIcon size={18} style={{ color: nodeColor }} />
          <div className="node-info-text">
            <span className="node-info-title">{getNodeTitle(selectedNode.type)}</span>
            <span className="node-info-id">ID: {selectedNode.id}</span>
          </div>
        </div>
      </div>

      {/* Connections Section */}
      <div className="properties-section">
        <h4>Conexiones</h4>
        <div className="connections-info">
          <div className="connection-row">
            <Link size={14} className="connection-icon input" />
            <span>Entradas:</span>
            <span className="connection-count">{inputConnections.length}</span>
          </div>
          <div className="connection-row">
            <Unlink size={14} className="connection-icon output" />
            <span>Salidas:</span>
            <span className="connection-count">{outputConnections.length}</span>
          </div>
        </div>
        {inputConnections.length > 0 && (
          <div className="connection-list">
            <span className="connection-list-title">Conectado desde:</span>
            {inputConnections.map((edge) => {
              const sourceNode = nodes.find((n) => n.id === edge.source);
              return (
                <div key={edge.id} className="connection-item">
                  <span>{getNodeTitle(sourceNode?.type)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Status Section */}
      <div className="properties-section">
        <h4>Estado</h4>
        <div className="status-info">
          {nodeData.hasOutput ? (
            <div className="status-badge success">
              <span className="status-dot"></span>
              Procesado
            </div>
          ) : (
            <div className="status-badge pending">
              <span className="status-dot"></span>
              Pendiente
            </div>
          )}
        </div>
      </div>

      {/* Output Preview Section */}
      {nodeData.outputUrl && (
        <div className="properties-section">
          <h4>Resultado</h4>
          <div className="output-preview-container">
            {nodeData.outputUrl.includes('.mp4') || nodeData.outputUrl.includes('video') ? (
              <video 
                src={nodeData.outputUrl} 
                controls 
                className="output-preview-media"
              />
            ) : (
              <img 
                src={nodeData.outputUrl} 
                alt="Output" 
                className="output-preview-media"
              />
            )}
            <a 
              href={nodeData.outputUrl} 
              download 
              className="output-download-btn"
            >
              <Download size={14} />
              Descargar
            </a>
          </div>
        </div>
      )}

      {/* Node-specific Properties */}
      {selectedNode.type === 'uploadImage' && nodeData.fileName && (
        <div className="properties-section">
          <h4>Archivo</h4>
          <div className="file-info">
            <span className="file-name">{nodeData.fileName}</span>
          </div>
        </div>
      )}

      {selectedNode.type === 'videoGeneration' && (
        <div className="properties-section">
          <h4>Configuración</h4>
          <div className="property-item">
            <label>Modelo:</label>
            <span className="property-value">bytedance/seedance-1-pro</span>
          </div>
          <div className="property-item">
            <label>Duración:</label>
            <span className="property-value">10 segundos</span>
          </div>
          <div className="property-item">
            <label>Resolución:</label>
            <span className="property-value">1080p</span>
          </div>
        </div>
      )}

      {selectedNode.type === 'imageEnhancement' && (
        <div className="properties-section">
          <h4>Configuración</h4>
          <div className="property-item">
            <label>Modelo:</label>
            <span className="property-value">flux-kontext-apps/restore-image</span>
          </div>
          <div className="property-item">
            <label>Formato:</label>
            <span className="property-value">PNG</span>
          </div>
        </div>
      )}

      {selectedNode.type === 'nanoBanana' && (
        <div className="properties-section">
          <h4>Configuración</h4>
          <div className="property-item">
            <label>Modelo:</label>
            <span className="property-value">google/nano-banana</span>
          </div>
        </div>
      )}

      {/* Position Info */}
      <div className="properties-section">
        <h4>Posición</h4>
        <div className="position-info">
          <div className="position-item">
            <label>X:</label>
            <span>{Math.round(selectedNode.position?.x || 0)}px</span>
          </div>
          <div className="position-item">
            <label>Y:</label>
            <span>{Math.round(selectedNode.position?.y || 0)}px</span>
          </div>
        </div>
      </div>
    </div>
  );
});

NodeProperties.displayName = 'NodeProperties';

export default NodeProperties;
